import React, { useContext, useEffect, useState, useMemo } from "react";
import "./RentalModal.css";
import { LiaTimesSolid } from "react-icons/lia";
import { TbGift, TbHorseToy, TbPaw, TbTruckLoading, TbMoped, TbTruckDelivery, TbCar } from "react-icons/tb";
import ServiceDetails from "../ServiceDetails/ServiceDetails";
import { useLanguage } from "../../LanguageProvider";
import CourierCargoForm from "./CourierCargoForm";
import { MallContext } from "../../MallContext";
import carIcon from "../../assets/car-convertible.svg";
import { ReactComponent as CarIcon } from "../../assets/car-convertible.svg";

// Seçilebilecek hizmet tipleri:
const SERVICE_TYPES = {
  VESTIYER: "vestiyer",
  COCUK: "cocuk",
  HAYVAN: "hayvan",
  HEDIYE: "gift",
  KARGO: "kargo",
  KURYE: "kurye",
};

const normalizeServiceType = (t) => {
  if (!t) return null;
  switch (t) {
    case "kid-car":
      return SERVICE_TYPES.COCUK;
    case "pet-car":
      return SERVICE_TYPES.HAYVAN;
    case "gift":
      return SERVICE_TYPES.HEDIYE;
    case "vestiyer":
      return SERVICE_TYPES.VESTIYER;
    default:
      return t; // zaten enum ise dokunma
  }
};

function RentalModal({
  isOpen = false,
  setIsOpen,
  type = "new",
  serviceType,
  onRequestCreated,
  requestID,
  willDeliverDateTime,
}) {
  const { currentServices, mallInfo } = useContext(MallContext);

  const [countItems, setCountItems] = useState(1);

  const { getTranslation } = useLanguage();
  // Hangi hizmetin seçildiğini takip eden state
  const [selectedService, setSelectedService] = useState(
    type === "extend" ? normalizeServiceType(serviceType) : null
  );

  // İkinci aşamada süre ayarlama örneği için state:
  const [timeInMinutes, setTimeInMinutes] = useState(75);

  // --- Araç uygunluğu helper'ı ---
  const hasAvailableVehicle = (vehicleType) => {
    const list = mallInfo?.vehicleInventory?.[vehicleType];
    if (!Array.isArray(list)) return false;
    return list.some((v) => v.status === "available");
  };

  const isKidCarAvailable = useMemo(
    () => hasAvailableVehicle("kid-car"),
    [mallInfo]
  );
  const isPetCarAvailable = useMemo(
    () => hasAvailableVehicle("pet-car"),
    [mallInfo]
  );

  // Hazır süre seçenekleri
  const timeOptions = [
    "30 " + getTranslation("min"),
    "1 " + getTranslation("hour"),
    "2 " + getTranslation("hour"),
    getTranslation("daily"),
  ];
  const countOptions = [
    getTranslation("onePiece"),
    getTranslation("fivePieces"),
    getTranslation("tenPieces"),
  ];
  const incrementCount = () => setCountItems((prev) => prev + 1);
  const decrementCount = () =>
    setCountItems((prev) => (prev > 1 ? prev - 1 : 1));
  const handleCountOptionClick = (option) => {
    switch (option) {
      case getTranslation("onePiece"):
        setCountItems(1);
        break;
      case getTranslation("fivePieces"):
        setCountItems(5);
        break;
      case getTranslation("tenPieces"):
        setCountItems(10);
        break;
      default:
        break;
    }
  };

  // Modal kapatma
  const closeModal = () => {
    setIsOpen(false);
    setSelectedService(null); // Modal kapatırken seçimi sıfırla
  };

  // Seçim butonlarına basılınca
  const handleServiceSelect = (service) => {
    setSelectedService(service);
  };

  // Süre 15 dk artırma/azaltma
  const incrementTime = () => {
    setTimeInMinutes((prev) => prev + 15);
  };

  const decrementTime = () => {
    setTimeInMinutes((prev) => (prev > 15 ? prev - 15 : prev));
  };

  useEffect(() => {
    if (isOpen && type === "extend") {
      setSelectedService(normalizeServiceType(serviceType));
    }
  }, [isOpen, serviceType, type]);

  // Hazır süre butonlarına tıklayınca
  const handleTimeOptionClick = (option) => {
    switch (option) {
      case "30 " + getTranslation("min"):
        setTimeInMinutes(30);
        break;
      case "1 " + getTranslation("hour"):
        setTimeInMinutes(60);
        break;
      case "2 " + getTranslation("hour"):
        setTimeInMinutes(120);
        break;
      case getTranslation("daily"):
        setTimeInMinutes(24 * 60);
        break;
      default:
        setTimeInMinutes(75);
    }
  };

  // Başlığı seçili hizmete göre değiştirmek için fonksiyon
  const getModalTitle = () => {
    switch (selectedService) {
      case SERVICE_TYPES.VESTIYER:
        return getTranslation("createCloakroomItemDeliveryRequest");
      case SERVICE_TYPES.COCUK:
        return getTranslation("rentToyStroller");
      case SERVICE_TYPES.HAYVAN:
        return getTranslation("rentPetVehicle");
      case SERVICE_TYPES.HEDIYE:
        return getTranslation("giftPackage");
      default:
        return getTranslation("createRentalService");
    }
  };

  const getExtendModalTitle = () => {
    const s = normalizeServiceType(selectedService);
    switch (s) {
      case SERVICE_TYPES.VESTIYER:
        return getTranslation("extendCloakroomDuration");
      case SERVICE_TYPES.COCUK:
        return getTranslation("extendToyStrollerDuration");
      case SERVICE_TYPES.HAYVAN:
        return getTranslation("extendPetStrollerDuration");
      case SERVICE_TYPES.HEDIYE:
        return getTranslation("giftPackage");
      default:
        return getTranslation("extendRentalService");
    }
  };

  // İlk aşamadaki butonları gösteren JSX
  const renderServiceSelection = () => {
    return (
      <div className="modal-body">
        {/* Vestiyer kapalı */}
        {/* <button
          className="modal-button vestiyer-talebi"
          onClick={() => handleServiceSelect(SERVICE_TYPES.VESTIYER)}
        >
          Vestiyer Talebi
          <TbTruckLoading size={20} />
        </button> */}

        {currentServices?.includes("gift") && (
          <button
            className="modal-button vestiyer-talebi"
            onClick={() => handleServiceSelect(SERVICE_TYPES.HEDIYE)}
          >
            {getTranslation("giftPackage")}
            <TbGift size={20} />
          </button>
        )}

        {currentServices?.includes("kid-car") && (
          <button
            className={`modal-button cocuk-araci${
              !isKidCarAvailable ? " disabled" : ""
            }`}
            onClick={() =>
              isKidCarAvailable && handleServiceSelect(SERVICE_TYPES.COCUK)
            }
            disabled={!isKidCarAvailable}
          >
            {getTranslation("toyStroller")}
            <CarIcon size={20} />
          </button>
        )}

        {currentServices?.includes("pet-car") && (
          <button
            className={`modal-button evcil-hayvan-araci${
              !isPetCarAvailable ? " disabled" : ""
            }`}
            onClick={() =>
              isPetCarAvailable && handleServiceSelect(SERVICE_TYPES.HAYVAN)
            }
            disabled={!isPetCarAvailable}
          >
            {getTranslation("petStroller")}
            <TbPaw size={20} />
          </button>
        )}

        {/* Kurye / Kargo ileride açılacak */}
        {/* <button
          className="modal-button vestiyer-talebi"
          onClick={() => handleRequestSelection("kurye")}
        >
          {getTranslation("CallCourier")}
          <TbMoped size={20} />
        </button>
        <button
          className="modal-button vestiyer-talebi"
          onClick={() => handleRequestSelection("kargo")}
        >
          {getTranslation("CallCargo")}
          <TbTruckDelivery size={20} />
        </button> */}
      </div>
    );
  };

  return (
    <>
      {isOpen && (
        <div className="modal-overlay">
          <div
            className={`modal-container${
              selectedService === "kargo" || selectedService === "kurye"
                ? " kargo-kurye-modal"
                : ""
            }`}
          >
            <div className="modal-header">
              <button className="close-button" onClick={closeModal}>
                <LiaTimesSolid size={22} color="rgba(152, 162, 179, 1)" />
              </button>
              <span
                style={{ paddingInline: "20px", boxSizing: "border-box" }}
              >
                {type === "new" ? getModalTitle() : getExtendModalTitle()}
                <div className="service-detail-subtitle">
                  {getTranslation("verifyAndConfirmInfo")}
                </div>
              </span>
            </div>

            {selectedService === null && type === "new" ? (
              renderServiceSelection()
            ) : selectedService === "kargo" || selectedService === "kurye" ? (
              <CourierCargoForm
                selectedService={selectedService}
                setIsOpen={setIsOpen}
                onRequestCreated={onRequestCreated}
              />
            ) : (
              <ServiceDetails
                timeInMinutes={timeInMinutes}
                incrementTime={incrementTime}
                decrementTime={decrementTime}
                timeOptions={timeOptions}
                handleTimeOptionClick={handleTimeOptionClick}
                setIsRentalModalOpen={setIsOpen}
                selectedService={selectedService}
                countOptions={countOptions}
                countItems={countItems}
                incrementCount={incrementCount}
                decrementCount={decrementCount}
                handleCountOptionClick={handleCountOptionClick}
                type={type}
                requestID={requestID}
                willDeliverDateTime={willDeliverDateTime}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default RentalModal;